// Purchases API
import { api, apiGet, apiPost, apiPatch, apiDelete } from './client'

export const purchasesApi = {
  // Purchase Orders
  listPurchaseOrders: (params?: PurchaseOrderListParams) =>
    apiGet<PaginatedResponse<PurchaseOrder>>('/purchase/orders', params),

  getPurchaseOrder: (id: string) =>
    apiGet<PurchaseOrder>(`/purchase/orders/${id}`),

  createPurchaseOrder: (data: PurchaseOrderCreate) =>
    apiPost<PurchaseOrder>('/purchase/orders', data),

  updatePurchaseOrder: (id: string, data: PurchaseOrderUpdate) =>
    apiPatch<PurchaseOrder>(`/purchase/orders/${id}`, data),

  deletePurchaseOrder: (id: string) =>
    apiDelete(`/purchase/orders/${id}`),

  approvePurchaseOrder: (id: string) =>
    apiPost<PurchaseOrder>(`/purchase/orders/${id}/approve`),

  orderPurchaseOrder: (id: string) =>
    apiPost<PurchaseOrder>(`/purchase/orders/${id}/order`),

  cancelPurchaseOrder: (id: string) =>
    apiPost<PurchaseOrder>(`/purchase/orders/${id}/cancel`),

  // Suppliers
  listSuppliers: (params?: SupplierListParams) =>
    apiGet<PaginatedResponse<Supplier>>('/purchase/suppliers', params),

  getSupplier: (id: string) =>
    apiGet<Supplier>(`/purchase/suppliers/${id}`),

  createSupplier: (data: SupplierCreate) =>
    apiPost<Supplier>('/purchase/suppliers', data),

  updateSupplier: (id: string, data: SupplierUpdate) =>
    apiPatch<Supplier>(`/purchase/suppliers/${id}`, data),

  deleteSupplier: (id: string) =>
    apiDelete(`/purchase/suppliers/${id}`),

  // Bills
  listBills: (params?: BillListParams) =>
    apiGet<PaginatedResponse<Bill>>('/purchase/bills', params),

  getBill: (id: string) =>
    apiGet<Bill>(`/purchase/bills/${id}`),

  createBill: (data: BillCreate) =>
    apiPost<Bill>('/purchase/bills', data),

  updateBill: (id: string, data: BillUpdate) =>
    apiPatch<Bill>(`/purchase/bills/${id}`, data),

  deleteBill: (id: string) =>
    apiDelete(`/purchase/bills/${id}`),

  payBill: (id: string, data: BillPaymentCreate) =>
    apiPost<BillPayment>(`/purchase/bills/${id}/pay`, data),

  // Supplier Payments
  listSupplierPayments: (params?: SupplierPaymentListParams) =>
    apiGet<PaginatedResponse<SupplierPayment>>('/purchase/payments', params),

  createSupplierPayment: (data: SupplierPaymentCreate) =>
    apiPost<SupplierPayment>('/purchase/payments', data),
}

// Types
export interface PaginationParams {
  page?: number
  page_size?: number
  search?: string
  sort_by?: string
  sort_order?: 'asc' | 'desc'
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

// Purchase Orders
export interface PurchaseOrderListParams extends PaginationParams {
  status?: 'draft' | 'pending' | 'approved' | 'ordered' | 'partial' | 'completed' | 'cancelled'
  supplier_id?: string
  warehouse_id?: string
  date_from?: string
  date_to?: string
}

export interface PurchaseOrder {
  id: string
  order_number: string
  supplier_id: string
  warehouse_id: string
  status: 'draft' | 'pending' | 'approved' | 'ordered' | 'partial' | 'completed' | 'cancelled'
  order_date: string
  expected_date?: string
  reference_number?: string
  notes?: string
  subtotal: number
  discount_amount: number
  tax_amount: number
  total_amount: number
  supplier: { id: string; name: string; code: string }
  warehouse: { id: string; name: string; code: string }
  items: PurchaseOrderItem[]
  created_at: string
  updated_at: string
  approved_at?: string
  ordered_at?: string
  completed_at?: string
  cancelled_at?: string
}

export interface PurchaseOrderItem {
  id: string
  product_id: string
  variant_id?: string
  quantity: number
  unit_price: number
  discount_percent: number
  tax_rate: number
  line_total: number
  notes?: string
  product: { id: string; name: string; sku: string; sale_price: number; images: string[] }
  variant?: { id: string; name: string; sku: string; sale_price: number; images: string[] }
}

export interface PurchaseOrderCreate {
  supplier_id: string
  warehouse_id: string
  order_date: string
  expected_date?: string
  reference_number?: string
  notes?: string
  items: Array<{
    product_id: string
    variant_id?: string
    quantity: number
    unit_price: number
    discount_percent?: number
    tax_rate?: number
    notes?: string
  }>
}

export interface PurchaseOrderUpdate {
  expected_date?: string
  reference_number?: string
  notes?: string
  items?: Array<{
    id?: string
    product_id: string
    variant_id?: string
    quantity: number
    unit_price: number
    discount_percent?: number
    tax_rate?: number
    notes?: string
  }>
}

// Suppliers
export interface SupplierListParams extends PaginationParams {
  status?: 'active' | 'inactive' | 'blocked'
  supplier_type?: string
  city?: string
}

export interface Supplier {
  id: string
  name: string
  trade_name?: string
  code: string
  supplier_type: 'manufacturer' | 'distributor' | 'wholesaler' | 'importer' | 'other'
  tax_id?: string
  national_id?: string
  email?: string
  phone?: string
  mobile?: string
  address?: string
  city?: string
  province?: string
  postal_code?: string
  website?: string
  contact_person?: string
  contact_phone?: string
  contact_email?: string
  payment_terms: number
  credit_limit: number
  currency: string
  status: 'active' | 'inactive' | 'blocked'
  notes?: string
  total_orders: number
  total_amount: number
  outstanding_balance: number
  created_at: string
  updated_at: string
}

export interface SupplierCreate {
  name: string
  trade_name?: string
  code: string
  supplier_type: 'manufacturer' | 'distributor' | 'wholesaler' | 'importer' | 'other'
  tax_id?: string
  national_id?: string
  email?: string
  phone?: string
  mobile?: string
  address?: string
  city?: string
  province?: string
  postal_code?: string
  website?: string
  contact_person?: string
  contact_phone?: string
  contact_email?: string
  payment_terms?: number
  credit_limit?: number
  currency?: string
  status?: 'active' | 'inactive' | 'blocked'
  notes?: string
}

export interface SupplierUpdate {
  name?: string
  trade_name?: string
  supplier_type?: 'manufacturer' | 'distributor' | 'wholesaler' | 'importer' | 'other'
  tax_id?: string
  national_id?: string
  email?: string
  phone?: string
  mobile?: string
  address?: string
  city?: string
  province?: string
  postal_code?: string
  website?: string
  contact_person?: string
  contact_phone?: string
  contact_email?: string
  payment_terms?: number
  credit_limit?: number
  currency?: string
  status?: 'active' | 'inactive' | 'blocked'
  notes?: string
}

// Bills
export interface BillListParams extends PaginationParams {
  status?: 'draft' | 'pending' | 'paid' | 'partial' | 'overdue' | 'cancelled'
  supplier_id?: string
  purchase_order_id?: string
  date_from?: string
  date_to?: string
  due_date_from?: string
  due_date_to?: string
}

export interface Bill {
  id: string
  bill_number: string
  supplier_id: string
  purchase_order_id?: string
  status: 'draft' | 'pending' | 'paid' | 'partial' | 'overdue' | 'cancelled'
  bill_date: string
  due_date: string
  reference_number?: string
  notes?: string
  subtotal: number
  discount_amount: number
  tax_amount: number
  total_amount: number
  paid_amount: number
  balance_amount: number
  supplier: { id: string; name: string; code: string }
  purchase_order?: { id: string; order_number: string }
  items: BillItem[]
  payments: BillPayment[]
  created_at: string
  updated_at: string
}

export interface BillItem {
  id: string
  product_id: string
  variant_id?: string
  quantity: number
  unit_price: number
  discount_percent: number
  tax_rate: number
  line_total: number
  product: { id: string; name: string; sku: string }
  variant?: { id: string; name: string; sku: string }
}

export interface BillCreate {
  supplier_id: string
  purchase_order_id?: string
  bill_date: string
  due_date: string
  reference_number?: string
  notes?: string
}

export interface BillUpdate {
  due_date?: string
  reference_number?: string
  notes?: string
}

export interface BillPayment {
  id: string
  bill_id: string
  amount: number
  payment_method: 'cash' | 'bank_transfer' | 'check' | 'card' | 'pos' | 'wallet'
  reference?: string
  payment_date: string
  notes?: string
  created_at: string
}

export interface BillPaymentCreate {
  amount: number
  payment_method: 'cash' | 'bank_transfer' | 'check' | 'card' | 'pos' | 'wallet'
  reference?: string
  payment_date: string
  notes?: string
}

// Supplier Payments
export interface SupplierPaymentListParams extends PaginationParams {
  supplier_id?: string
  payment_method?: string
  date_from?: string
  date_to?: string
}

export interface SupplierPayment {
  id: string
  supplier_id: string
  amount: number
  payment_method: 'cash' | 'bank_transfer' | 'check' | 'card' | 'pos' | 'wallet'
  reference?: string
  payment_date: string
  notes?: string
  supplier: { id: string; name: string; code: string }
  bills: Array<{ id: string; bill_number: string; amount: number }>
  created_at: string
}

export interface SupplierPaymentCreate {
  supplier_id: string
  amount: number
  payment_method: 'cash' | 'bank_transfer' | 'check' | 'card' | 'pos' | 'wallet'
  reference?: string
  payment_date: string
  notes?: string
  bill_allocations?: Array<{ bill_id: string; amount: number }>
}